import React, { useEffect, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { getDataToolsFieldValues } from "../../Api/Api";
import { prettyEnum } from "./dataToolsUtils";

const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white";

/** One "Only include rows where … " line: field → condition → value. */
const ConditionRow = ({ dataset, filter, operators, workshopId, onChange, onRemove }) => {
    const field = dataset.fields.find((f) => f.key === filter.field) || dataset.fields[0];
    const fieldOps = operators[field.type] || [];
    const op = fieldOps.find((o) => o.key === filter.operator) || fieldOps[0];

    const changeField = (key) => {
        const next = dataset.fields.find((f) => f.key === key);
        const nextOp = (operators[next.type] || [])[0];
        onChange({ field: key, operator: nextOp?.key, value: nextOp?.input === "DAYS" ? "30" : "", valueTo: "", values: [] });
    };

    const changeOperator = (key) => {
        const nextOp = fieldOps.find((o) => o.key === key);
        onChange({ ...filter, operator: key, value: nextOp?.input === "DAYS" ? filter.value || "30" : filter.value });
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
                <select className={inputClass} value={field.key} onChange={(e) => changeField(e.target.value)} aria-label="Field">
                    {dataset.fields.map((f) => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                </select>
                <button
                    onClick={onRemove}
                    className="p-2 mt-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Remove this condition"
                >
                    <FaTimes />
                </button>
            </div>
            <select className={inputClass} value={op?.key} onChange={(e) => changeOperator(e.target.value)} aria-label="Condition">
                {fieldOps.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                ))}
            </select>
            <ValueInput
                field={field}
                op={op}
                filter={filter}
                dataset={dataset}
                workshopId={workshopId}
                onChange={onChange}
            />
        </div>
    );
};

const ValueInput = ({ field, op, filter, dataset, workshopId, onChange }) => {
    if (!op || op.input === "NONE") return null;

    const htmlType = field.type === "DATE" ? "date" : field.type === "MONEY" || field.type === "NUMBER" ? "number" : "text";
    const placeholder = field.type === "MONEY" ? "Amount in ₹" : field.type === "NUMBER" ? "Number" : "Type here…";

    if (op.input === "MULTI") {
        const selected = filter.values || [];
        const toggle = (opt) =>
            onChange({ ...filter, values: selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt] });
        return (
            <div className="flex flex-wrap gap-2">
                {field.options.map((opt) => {
                    const on = selected.includes(opt);
                    return (
                        <button
                            key={opt}
                            onClick={() => toggle(opt)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                                on ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-gray-300 text-gray-600 hover:border-teal-500"
                            }`}
                        >
                            {on ? "✓ " : ""}{prettyEnum(opt)}
                        </button>
                    );
                })}
            </div>
        );
    }

    if (op.input === "DAYS") {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-600">
                <input
                    type="number"
                    min="1"
                    className={`${inputClass} w-28`}
                    value={filter.value}
                    onChange={(e) => onChange({ ...filter, value: e.target.value })}
                />
                <span>days</span>
            </div>
        );
    }

    if (op.input === "RANGE") {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type={htmlType} className={inputClass} value={filter.value}
                       onChange={(e) => onChange({ ...filter, value: e.target.value })} placeholder="From" />
                <span>and</span>
                <input type={htmlType} className={inputClass} value={filter.valueTo}
                       onChange={(e) => onChange({ ...filter, valueTo: e.target.value })} placeholder="To" />
            </div>
        );
    }

    if (field.suggest) {
        return (
            <SuggestInput
                dataset={dataset}
                field={field}
                workshopId={workshopId}
                value={filter.value}
                onChange={(value) => onChange({ ...filter, value })}
            />
        );
    }

    return (
        <input
            type={htmlType}
            className={inputClass}
            value={filter.value}
            placeholder={placeholder}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
        />
    );
};

/** Text box that suggests real values (e.g. branch names) as you type. */
const SuggestInput = ({ dataset, field, workshopId, value, onChange }) => {
    const [suggestions, setSuggestions] = useState([]);
    const timer = useRef(null);
    const listId = `dt-suggest-${dataset.key}-${field.key}`;

    useEffect(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            getDataToolsFieldValues(workshopId, dataset.key, field.key, value || "")
                .then((res) => setSuggestions(res.data))
                .catch(() => setSuggestions([]));
        }, 300);
        return () => clearTimeout(timer.current);
    }, [workshopId, dataset.key, field.key, value]);

    return (
        <>
            <input
                className={inputClass}
                list={listId}
                value={value}
                placeholder={`Type or pick a ${field.label.toLowerCase()}…`}
                onChange={(e) => onChange(e.target.value)}
            />
            <datalist id={listId}>
                {suggestions.map((s) => (
                    <option key={s} value={s} />
                ))}
            </datalist>
        </>
    );
};

export default ConditionRow;
